import { useCallback } from 'react'
import { useGlobalEvent } from '@highlight/ipc/react'
import { useSetSidepanelState } from '@renderer/overlay/atoms'
import { useSetSidepanelHotkeyOpened } from '@renderer/overlay/Sidepanel/atoms'
import { useViewActions } from '@renderer/overlay/Sidepanel/view-atoms'
import { remoteOpeners, type OpenViewEventPayload } from './remote-openers'

/**
 * Listens for `sidepanel:openView` global events and navigates the overlay
 * sidepanel to the requested View.
 *
 * Used by the main process (Slack deeplinks) and the base window (e.g. artifact
 * links in Live Context) to route content into the overlay.
 */
export function useSidepanelEventHandler() {
  const setSidepanelState = useSetSidepanelState()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()
  const { openView } = useViewActions()

  const onOpenView = useCallback(
    (payload: OpenViewEventPayload) => {
      // Senders live in other processes, so a bad id or a context missing the
      // one field the View needs means a stale build or a malformed deeplink —
      // surface it rather than pushing an unrenderable view.
      const open = payload?.id ? remoteOpeners[payload.id] : undefined
      if (!open) {
        console.warn('Ignoring sidepanel:openView for unknown view id:', payload?.id)
        return
      }

      setHotkeyOpened(true)
      setSidepanelState('open')

      // The panel still opens on a rejected context: the sender has already
      // shown the window, so swallowing the event entirely would leave a
      // clicked link doing nothing at all. Only the broken View is skipped.
      if (!open(openView, payload.context)) {
        console.warn('Ignoring sidepanel:openView with unusable context for view:', payload.id, payload.context)
      }
    },
    [setHotkeyOpened, setSidepanelState, openView],
  )

  useGlobalEvent('sidepanel:openView', onOpenView)
}
